import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUserAuth } from "./context/userAuthContext";
import { db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import {
  User,
  Menu,
  X,
  LogIn,
  UserPlus,
  Home,
  MapPin,
  FileText,
} from "lucide-react";

const PolloFelizNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserAuth();
  const [userPhotoUrl, setUserPhotoUrl] = useState("");
  const [userName, setUserName] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (user?.uid) {
      const docRef = doc(db, "Users", user.uid);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setUserPhotoUrl(userData.photoUrl || "");
          setUserName(userData.nombre || "");
          const adminFlag =
            userData.rol === "admin" ||
            userData.role === "admin" ||
            userData.isAdmin === true ||
            userData.type === "admin";
          setIsAdmin(!!adminFlag);
        }
      });
      return () => unsubscribe();
    } else {
      setUserPhotoUrl("");
      setUserName("");
      setIsAdmin(false);
    }
  }, [user]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isLoggedIn = !!user;

  return (
    <nav
      className={`bg-gradient-to-r from-[#FF0000] via-[#FF0000] to-[#CC0000] fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "shadow-2xl backdrop-blur-lg" : "shadow-lg"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Mobile Menu Button (left) */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white text-[#FF0000] hover:bg-gray-50 transition-all duration-200"
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" strokeWidth={2} />
            ) : (
              <Menu className="h-6 w-6" strokeWidth={2} />
            )}
          </button>

          {/* Logo (centered on mobile, left on desktop) */}
          <div className="flex-1 md:flex-initial flex justify-center md:justify-start">
            <Link
              to="/"
              className="flex items-center hover:opacity-90 transition-opacity duration-300"
            >
              <img
                src="https://firebasestorage.googleapis.com/v0/b/newpollofeliz.appspot.com/o/logos%2Flogobarrapf.png?alt=media&token=26f1f56a-4c18-4d93-9574-c1762bda9d26"
                alt="Pollo Feliz Logo"
                className="h-12 sm:h-14 w-auto"
              />
            </Link>
          </div>

          {/* User Icon (right, mobile only) */}
          <button
            onClick={() => navigate(isLoggedIn ? "/profile" : "/login")}
            className="md:hidden p-2 rounded-lg bg-white text-[#FF0000] hover:bg-gray-50 transition-all duration-200"
            aria-label={isLoggedIn ? "Perfil" : "Iniciar sesión"}
          >
            {isLoggedIn && userPhotoUrl ? (
              <img
                src={userPhotoUrl}
                alt="Profile"
                className="w-6 h-6 rounded-full"
              />
            ) : (
              <User className="h-6 w-6" strokeWidth={2} />
            )}
          </button>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            <Link
              to="/menu"
              className={`group relative flex items-center px-3 lg:px-5 py-2.5 font-semibold text-sm lg:text-base uppercase tracking-wide transition-all duration-200 rounded-lg ${
                isActive("/menu")
                  ? "bg-white text-[#FF0000] shadow-md"
                  : "bg-white/95 text-[#FF0000] hover:bg-white hover:shadow-md"
              }`}
            >
              <Home className="w-4 h-4 lg:w-5 lg:h-5 mr-2" strokeWidth={2} />
              <span className="hidden lg:inline">Menú</span>
              {isActive("/menu") && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF0000]"></div>
              )}
            </Link>

            <Link
              to="/sucursales"
              className={`group relative flex items-center px-3 lg:px-5 py-2.5 font-semibold text-sm lg:text-base uppercase tracking-wide transition-all duration-200 rounded-lg ${
                isActive("/sucursales")
                  ? "bg-white text-[#FF0000] shadow-md"
                  : "bg-white/95 text-[#FF0000] hover:bg-white hover:shadow-md"
              }`}
            >
              <MapPin className="w-4 h-4 lg:w-5 lg:h-5 mr-2" strokeWidth={2} />
              <span className="hidden lg:inline">Sucursales</span>
              {isActive("/sucursales") && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF0000]"></div>
              )}
            </Link>

            <Link
              to="/facturacion"
              className={`group relative flex items-center px-3 lg:px-5 py-2.5 font-semibold text-sm lg:text-base uppercase tracking-wide transition-all duration-200 rounded-lg ${
                isActive("/facturacion")
                  ? "bg-white text-[#FF0000] shadow-md"
                  : "bg-white/95 text-[#FF0000] hover:bg-white hover:shadow-md"
              }`}
            >
              <FileText
                className="w-4 h-4 lg:w-5 lg:h-5 mr-2"
                strokeWidth={2}
              />
              <span className="hidden lg:inline">Facturación</span>
              {isActive("/facturacion") && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF0000]"></div>
              )}
            </Link>

            {/* Separator */}
            <div className="w-px h-8 bg-white/30 mx-1 lg:mx-2"></div>

            {/* Auth Section */}
            {isLoggedIn ? (
              <button
                onClick={() => navigate("/profile")}
                className="group flex items-center px-3 lg:px-5 py-2.5 bg-white text-[#FF0000] hover:shadow-md transition-all duration-200 font-semibold text-sm lg:text-base uppercase tracking-wide rounded-lg"
                aria-label="Perfil"
              >
                {userPhotoUrl ? (
                  <img
                    src={userPhotoUrl}
                    alt="Profile"
                    className="w-6 h-6 rounded-full mr-2"
                  />
                ) : (
                  <User
                    className="w-4 h-4 lg:w-5 lg:h-5 mr-2"
                    strokeWidth={2}
                  />
                )}
                <span className="max-w-24 lg:max-w-32 truncate hidden lg:inline">
                  {userName || "Perfil"}
                </span>
              </button>
            ) : (
              <div className="flex space-x-1 lg:space-x-2">
                <Link
                  to="/login"
                  className={`group relative flex items-center px-3 lg:px-5 py-2.5 font-semibold text-sm lg:text-base uppercase tracking-wide transition-all duration-200 rounded-lg ${
                    isActive("/login")
                      ? "bg-white text-[#FF0000] shadow-md"
                      : "bg-white/95 text-[#FF0000] hover:bg-white hover:shadow-md"
                  }`}
                >
                  <LogIn
                    className="w-4 h-4 lg:w-5 lg:h-5 mr-2"
                    strokeWidth={2}
                  />
                  <span className="hidden lg:inline">Login</span>
                </Link>

                <Link
                  to="/signup"
                  className={`group relative flex items-center px-3 lg:px-5 py-2.5 font-semibold text-sm lg:text-base uppercase tracking-wide transition-all duration-200 rounded-lg ${
                    isActive("/signup")
                      ? "bg-white text-[#FF0000] shadow-md"
                      : "bg-white/95 text-[#FF0000] hover:bg-white hover:shadow-md"
                  }`}
                >
                  <UserPlus
                    className="w-4 h-4 lg:w-5 lg:h-5 mr-2"
                    strokeWidth={2}
                  />
                  <span className="hidden lg:inline">Registro</span>
                </Link>
              </div>
            )}
            {isLoggedIn && isAdmin && (
              <Link
                to="/admin"
                className={`group relative flex items-center px-3 lg:px-5 py-2.5 font-semibold text-sm lg:text-base uppercase tracking-wide transition-all duration-200 rounded-lg bg-white text-black hover:bg-gray-100 border border-black`}
              >
                Admin
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-6 space-y-2 bg-gradient-to-b from-[#FF0000] to-[#CC0000]">
          <Link
            to="/menu"
            className={`flex items-center px-4 py-3.5 font-bold uppercase tracking-wide transition-all duration-300 rounded-xl ${
              isActive("/menu")
                ? "bg-[#FFBC00] text-[#FF0000] shadow-lg scale-[0.98]"
                : "bg-white/90 text-[#FF0000] hover:bg-[#FFBC00] active:scale-[0.98]"
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <Home className="mr-3 h-5 w-5" strokeWidth={2.5} />
            Menú
          </Link>

          <Link
            to="/sucursales"
            className={`flex items-center px-4 py-3.5 font-bold uppercase tracking-wide transition-all duration-300 rounded-xl ${
              isActive("/sucursales")
                ? "bg-[#FFBC00] text-[#FF0000] shadow-lg scale-[0.98]"
                : "bg-white/90 text-[#FF0000] hover:bg-[#FFBC00] active:scale-[0.98]"
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <MapPin className="mr-3 h-5 w-5" strokeWidth={2.5} />
            Sucursales
          </Link>

          <Link
            to="/facturacion"
            className={`flex items-center px-4 py-3.5 font-bold uppercase tracking-wide transition-all duration-300 rounded-xl ${
              isActive("/facturacion")
                ? "bg-[#FFBC00] text-[#FF0000] shadow-lg scale-[0.98]"
                : "bg-white/90 text-[#FF0000] hover:bg-[#FFBC00] active:scale-[0.98]"
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            <FileText className="mr-3 h-5 w-5" strokeWidth={2.5} />
            Facturación
          </Link>

          {/* Separator */}
          <div className="h-px bg-white/20 my-3"></div>

          {/* Auth Section */}
          {isLoggedIn ? (
            <Link
              to="/profile"
              className="flex items-center px-4 py-3.5 bg-[#FFBC00] text-[#FF0000] font-bold uppercase tracking-wide rounded-xl shadow-lg active:scale-[0.98] transition-all duration-300"
              onClick={() => setMobileMenuOpen(false)}
            >
              {userPhotoUrl ? (
                <img
                  src={userPhotoUrl}
                  alt="Profile"
                  className="w-6 h-6 rounded-full mr-3 ring-2 ring-[#FF0000]"
                />
              ) : (
                <User className="mr-3 h-5 w-5" strokeWidth={2.5} />
              )}
              <span className="truncate">{userName || "Mi Perfil"}</span>
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className={`flex items-center px-4 py-3.5 font-bold uppercase tracking-wide transition-all duration-300 rounded-xl ${
                  isActive("/login")
                    ? "bg-[#FFBC00] text-[#FF0000] shadow-lg scale-[0.98]"
                    : "bg-white/90 text-[#FF0000] hover:bg-[#FFBC00] active:scale-[0.98]"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <LogIn className="mr-3 h-5 w-5" strokeWidth={2.5} />
                Login
              </Link>

              <Link
                to="/signup"
                className={`flex items-center px-4 py-3.5 font-bold uppercase tracking-wide transition-all duration-300 rounded-xl ${
                  isActive("/signup")
                    ? "bg-[#FFBC00] text-[#FF0000] shadow-lg scale-[0.98]"
                    : "bg-white/90 text-[#FF0000] hover:bg-[#FFBC00] active:scale-[0.98]"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <UserPlus className="mr-3 h-5 w-5" strokeWidth={2.5} />
                Registro
              </Link>
            </>
          )}
          {isLoggedIn && isAdmin && (
            <Link
              to="/admin"
              className="flex items-center px-4 py-3.5 font-bold uppercase tracking-wide transition-all duration-300 rounded-xl bg-white text-black active:scale-[0.98]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Admin
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default PolloFelizNavbar;
